import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { X, Check } from 'lucide-react';
import { isNative } from '../utils/platform';
import { restorePurchases } from '../services/purchases';
import { usePremiumCopy } from '../hooks/usePremiumCopy';

const ROTATING_TITLES = [
  'Azi ai acces la ceva special.',
  'Ce-ar fi dacă astăzi ar fi altfel?',
  'Noi aducem liniștea. Tu aduci curajul.',
];

// ── Premium Popup Context ────────────────────────────────────────────
const PremiumPopupContext = createContext(null);

export const usePremiumPopup = () => {
  const ctx = useContext(PremiumPopupContext);
  if (!ctx) return { triggerPremiumPopup: () => {} }; // safe fallback
  return ctx;
};

export const PremiumPopupProvider = ({ children }) => {
  const [show, setShow] = useState(false);
  const [shownThisSession, setShownThisSession] = useState(false);

  // Preview mode: ?showpremium=1 in URL → show immediately (kept in Provider for early mount)

  const triggerPremiumPopup = useCallback((mode) => {
    if (mode === 'force') {
      setShow(true);
      return;
    }

    // Don't show if already shown this session
    if (shownThisSession) return;

    // Don't show for premium users
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user || user.premium === true) return;

    // Don't show if user dismissed today
    const laterToday = localStorage.getItem('premiumPopupLaterToday');
    if (laterToday) {
      const laterDate = new Date(JSON.parse(laterToday));
      const today = new Date();
      if (
        laterDate.getDate() === today.getDate() &&
        laterDate.getMonth() === today.getMonth() &&
        laterDate.getFullYear() === today.getFullYear()
      ) {
        return;
      }
    }

    setShow(true);
    setShownThisSession(true);
  }, [shownThisSession]);

  const hidePopup = useCallback(() => {
    setShow(false);
  }, []);

  return (
    <PremiumPopupContext.Provider value={{ show, triggerPremiumPopup, hidePopup }}>
      {children}
    </PremiumPopupContext.Provider>
  );
};

// ── Premium Popup UI ──────────────────────────────────────────────────
const PremiumPopup = () => {
  const navigate = useNavigate();
  const { show, hidePopup, triggerPremiumPopup } = usePremiumPopup();
  const { copy } = usePremiumCopy();
  const [imageVersion] = useState(() => `?v=${Date.now()}`);
  const [title] = useState(() => ROTATING_TITLES[Math.floor(Math.random() * ROTATING_TITLES.length)]);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState(null);

  // Preview mode: ?showpremium=1 in URL
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('showpremium') === '1') {
      // Force show — bypass premium/session checks
      triggerPremiumPopup('force');
    }
  }, [triggerPremiumPopup]);

  const handleRestore = async () => {
    setRestoreLoading(true);
    setRestoreMessage(null);
    try {
      await restorePurchases();
      setRestoreMessage('Achizițiile au fost restaurate cu succes.');
    } catch (err) {
      console.error('Eroare la restaurare:', err);
      setRestoreMessage('Nu s-a putut restaura. Încearcă din nou.');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleClose = () => {
    localStorage.setItem('premiumPopupLastShown', JSON.stringify(new Date()));
    hidePopup();
  };

  const handleLater = () => {
    localStorage.setItem('premiumPopupLaterToday', JSON.stringify(new Date()));
    hidePopup();
  };

  const handleActivate = () => {
    localStorage.setItem('premiumPopupLastShown', JSON.stringify(new Date()));
    hidePopup();
    navigate('/premium');
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: '100%' }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: '100%' }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed inset-0 z-[9999] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Full-screen background image */}
          <div className="absolute inset-0">
            <img
              src={`/img/fundal_premiumpopup_1.jpg${imageVersion}`}
              alt=""
              className="w-full h-full object-cover"
            />
            {/* Gradient overlay — bottom half fades to solid for readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E3D2B] via-[#2E3D2B]/80 to-transparent" />
          </div>

          {/* X button */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 w-10 h-10 rounded-full backdrop-blur-sm bg-white/20 flex items-center justify-center z-10"
            aria-label="Închide"
          >
            <X size={20} className="text-white" />
          </button>

          {/* Content pinned to bottom */}
          <div className="absolute bottom-0 left-0 right-0 px-6 pb-10 pt-6">
            <div className="w-full max-w-lg mx-auto">
              {/* Title */}
              <h2 className="text-2xl font-semibold text-white font-poppins mb-4 leading-tight">
                {title}
              </h2>

              {/* Benefits */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={13} className="text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[15px] font-semibold text-white font-poppins mb-0.5">
                      Experiențe create pentru realitatea ta
                    </p>
                    <p className="text-[13px] text-white/60 font-poppins leading-snug">
                      Când ai nevoie de liniște, de curaj sau de un nou început.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-accent flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check size={13} className="text-white" strokeWidth={3} />
                  </div>
                  <div className="flex-1">
                    <p className="text-[15px] font-semibold text-white font-poppins mb-0.5">
                      Totul în limba ta, rostit de oameni care te înțeleg
                    </p>
                    <p className="text-[13px] text-white/60 font-poppins leading-snug">
                      Pentru că vindecarea începe când te simți acasă.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA principal — text editat din admin (Premium copy → cta.button) */}
              <button
                onClick={handleActivate}
                className="w-full bg-accent text-white font-semibold py-4 rounded-full shadow-lg font-poppins text-base tracking-wide"
              >
                {copy.cta.button}
              </button>

              {/* CTA secundar */}
              <button
                onClick={handleLater}
                className="w-full text-white/50 text-sm font-medium font-poppins mt-3 mb-1"
              >
                Mai târziu
              </button>

              {/* Restore purchases — only on native */}
              {isNative() && (
                <div className="flex flex-col items-center mt-1">
                  <button
                    onClick={handleRestore}
                    disabled={restoreLoading}
                    className="text-xs font-poppins text-white/30 transition-colors disabled:opacity-50"
                  >
                    {restoreLoading ? 'Se restaurează...' : 'Restaurează achizițiile'}
                  </button>
                  {restoreMessage && (
                    <p className="text-xs text-center text-white/60 mt-1 font-poppins">{restoreMessage}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PremiumPopup;
